import { ColaboradorList } from "../../../enums/tipo-vinculo.enum"
import { FieldError, formSelectClass } from "@/shared/components/forms/FormFieldFeedback";
import { PersonFormValues } from "@/modules/people/presentation/person.schema";
import { PersonProfilesFormMethods } from "./profile.types"
import { useFormState } from "react-hook-form";
import { Controller } from "react-hook-form"
import { useEffect, useState } from "react"
import { readFuncao } from "@/modules/structure/funcao/funcao.service"
import { LotacaoService } from "@/modules/business/lotacoes/lotacao.service"
import { ReadLotacaoDto } from "@/api.types"
import { SearchDtoLotacoes } from "@/shared/types/search.dto"
import { DataAmigavel } from "@/shared/utils/datas"
import { TipoLotacaoList } from "@/shared/constants/enumlist"
import Links from "@/shared/constants/navegacao"
import { Link } from "react-router-dom"

interface Funcao {
  id: number
  nome: string
}

interface ColaboradorProfileProps {
  methods: PersonProfilesFormMethods
  podeVerColaborador: boolean
}

export function ColaboradorProfile({ methods, podeVerColaborador }: ColaboradorProfileProps) {
  const { errors } = useFormState<PersonFormValues>();
  const colaboradorAtivo = methods.watch('colaborador.ativo')
  const colaboradorId = methods.watch('colaborador.id')
  const [funcoes, setFuncoes] = useState<Funcao[]>([])
  const [lotacoesAtuais, setLotacoesAtuais] = useState<ReadLotacaoDto[]>([])

  const existeRegistro = !!colaboradorId
  const deveMostrarCampos = colaboradorAtivo || existeRegistro
  const camposDesabilitados = existeRegistro && !colaboradorAtivo

  useEffect(() => {
    readFuncao({ take: 1000, orderBy: 'ASC', orderField: 'nome' })
      .then(([data]) => setFuncoes(data))
      .catch(() => setFuncoes([]))
  }, [])

  useEffect(() => {
    if (colaboradorId) {
      const lotacaoService = new LotacaoService();
      const searchParams: SearchDtoLotacoes = {
        skip: 0,
        take: 9000,
        id_colaborador: colaboradorId,
      } as SearchDtoLotacoes;

      lotacaoService
        .Enumerate(searchParams)
        .then((result) => {
          setLotacoesAtuais(result[0]);
        })
        .catch((error) => {
          console.warn('Erro ao carregar lotações:', error);
        });
    }
  }, [colaboradorId]);

  if (!podeVerColaborador) {
    return null
  }

  return (
    <div className="card mb-3">
      <div className="card-header">
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="colaborador_ativo"
            {...methods.register("colaborador.ativo")}
          />
          <label className="form-check-label" htmlFor="colaborador_ativo">
            Habilitar Perfil de Colaborador(a)
          </label>
        </div>
      </div>

      {deveMostrarCampos && (
        <div className="card-body row">
          <div className="col-md-9">
            <div className="row">
              <div className="form-group mb-3 col-md-3">
                <label htmlFor="colaborador_tipo_vinculo">
                  Tipo de Vínculo{colaboradorAtivo && <span className="text-danger"> *</span>}
                </label>
                <select
                  id="colaborador_tipo_vinculo"
                  className={formSelectClass(!!errors.colaborador?.tipo_vinculo)}
                  disabled={camposDesabilitados}
                  {...methods.register('colaborador.tipo_vinculo')}
                >
                  {ColaboradorList.map(item => (
                    <option key={item.value} value={item.value}>{item.name}</option>
                  ))}
                </select>
                <FieldError message={errors.colaborador?.tipo_vinculo?.message} />
              </div>

              <div className="col-md-4">
                <label htmlFor="colaborador_funcao" className="form-label">Função:</label>
                <Controller
                  name="colaborador.funcao.id"
                  control={methods.control}
                  render={({ field }) => (
                    <select
                      id="colaborador_funcao"
                      className="form-select"
                      value={field.value || ''}
                      disabled={camposDesabilitados}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === '' ? null : Number(value));
                      }}
                      onBlur={field.onBlur}
                    >
                      <option value="">Selecione uma função</option>
                      {funcoes.map(funcao => (
                        <option key={funcao.id} value={funcao.id}>{funcao.nome}</option>
                      ))}
                    </select>
                  )}
                />
              </div>

              <div className="col-md-5">
                <label htmlFor="colaborador_orgao_origem" className="form-label">Órgão de Origem/Empresa:</label>
                <input
                  id="colaborador_orgao_origem"
                  type="text"
                  className="form-control"
                  disabled={camposDesabilitados}
                  {...methods.register('colaborador.orgao_origem')}
                />
              </div>

              <div className="col-md-3">
                <div className="form-check ms-3 mt-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="colaborador_vinculo_direto"
                    disabled={camposDesabilitados}
                    {...methods.register("colaborador.vinculo_direto")}
                  />
                  <label className="form-check-label" htmlFor="colaborador_vinculo_direto">
                    Vínculo Direto
                  </label>
                </div>

                <div className="form-check ms-3 mt-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="colaborador_cessao_onerosa"
                    disabled={camposDesabilitados}
                    {...methods.register("colaborador.cessao_onerosa")}
                  />
                  <label className="form-check-label" htmlFor="colaborador_cessao_onerosa">
                    Cessão Onerosa
                  </label>
                </div>
              </div>

              <div className="col-md-4">
                <label htmlFor="colaborador_anotacao" className="form-label">Anotações:</label>
                <input
                  id="colaborador_anotacao"
                  type="text"
                  className="form-control"
                  disabled={camposDesabilitados}
                  {...methods.register('colaborador.anotacao')}
                />
              </div>

            </div>
          </div>

          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <label htmlFor="colaborador_data_cessao" className="form-label">Data Cessão/Início:</label>
                  <input
                    id="colaborador_data_cessao"
                    type="date"
                    className="form-control"
                    disabled={camposDesabilitados}
                    {...methods.register('colaborador.data_cessao')}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="colaborador_data_saida" className="form-label">Data Saída:</label>
                  <input
                    id="colaborador_data_saida"
                    type="date"
                    className="form-control"
                    disabled={camposDesabilitados}
                    {...methods.register('colaborador.data_saida')}
                  />
                </div>
              </div>
            </div>
          </div>

          {lotacoesAtuais.length > 0 && (
            <div className="mt-4">
              <table className="table table-sm table-bordered table-hover table-striped">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th className="text-center">Início</th>
                    <th className="text-center">Término</th>
                    <th>Local</th>
                  </tr>
                </thead>
                <tbody>
                  {lotacoesAtuais.map((lotacao, index) => {
                    const local = lotacao.defensoria?.nome || lotacao.nucleo?.nome || lotacao.setor?.nome || '-';
                    return (
                      <tr key={index}>
                        <td>{TipoLotacaoList.find((a) => a.value === lotacao.tipo)?.name}</td>
                        <td className="text-center">{lotacao.data_inicial ? DataAmigavel(lotacao.data_inicial) : '-'}</td>
                        <td className="text-center">{lotacao.data_final ? DataAmigavel(lotacao.data_final) : '-'}</td>
                        <td>
                          {lotacao.id ? (
                            <Link to={`${Links.Vinculos_Editar}/${lotacao.id}`} target="_blank" title="Editar lotação"  >
                              {local}
                            </Link>
                          ) : (
                            local
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

